import type { ReactElement } from "react";
import { useEffect, useRef, useMemo, useReducer } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";
import type { Primitive } from "@similarweb/sw-track";

import { showSuccessToast, showErrorToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { EMPTY_COMPETITOR } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/LLMSetupConst";
import {
    CampaignEditContext,
    campaignEditReducer,
    initialEditState,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/CampaignEditContext";
import { CampaignSettingsModal } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/CampaignSettingsModal";
import { getUniqueDomainUrls } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/utils";
import type { IUpdateCampaignCompetitor } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/llm-tracker-service/llmTrackerCampignsQueries";
import type {
    ExplicitLLModel,
    ICampaignModalData,
    ICampaignDomain,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import {
    LLModel,
    AI_TRACKER_WEEKLY_REPORT_SUBSCRIPTION,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { WebsitePageSearchTypeNames } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import { SwRedux } from "app/services/SwRedux";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

import {
    useUpdateCampaign,
    useWeeklyReportSubscription,
} from "../../../shared/llm-tracker-service/useLLMTrackerCampaigns";

interface IEditCampaignModalHook {
    openModal: (campaign: ICampaignModalData | undefined, isReadOnly: boolean) => void;
    modal: ReactElement | null;
}

interface ISaveTrackEvent {
    isChanged: boolean;
    parameters?: Record<string, Primitive>;
}

export const useEditCampaignModal = (): IEditCampaignModalHook => {
    const {
        params: { settings },
    } = useCurrentStateAndParams();
    const campaignToEditRef = useRef<ICampaignModalData | undefined>(undefined);
    const isOpen = settings === "true";

    useEffect(() => {
        return () => {
            void proNavigator.updateParams({ settings: undefined }, { reload: false });
        };
    }, []);

    const [state, dispatch] = useReducer(campaignEditReducer, initialEditState);
    const {
        campaignName,
        selectedBrands,
        initialDomains,
        currentDomains,
        initialCompetitors,
        currentCompetitors,
        initialModels,
        currentModels,
        ownerId,
        initialWeeklyReportSubscribed,
        isWeeklyReportSubscribed,
        initialIncludeSubdomains,
        includeSubdomains,
    } = state;
    const isNameChanged = useMemo<boolean>(
        () => !!campaignToEditRef.current && campaignToEditRef.current.name !== campaignName,
        [campaignToEditRef, campaignName],
    );

    const areBrandsChanged = useMemo<boolean>(() => {
        if (!campaignToEditRef.current) {
            return false;
        }

        const currentBrands = (campaignToEditRef.current.brands ?? []).map((b) => b.toLowerCase()).sort();
        const selectedBrandsLower = selectedBrands.map((b) => b.toLowerCase()).sort();
        if (currentBrands.length !== selectedBrandsLower.length) {
            return true;
        }

        return currentBrands.some((brand, index) => brand !== selectedBrandsLower[index]);
    }, [campaignToEditRef, selectedBrands]);

    const areDomainsChanged = useMemo<boolean>(() => {
        const initialNames = initialDomains.map(({ name }) => name).sort();
        const currentNames = currentDomains.map(({ name }) => name).sort();

        return (
            initialNames.length !== currentNames.length ||
            initialNames.some((name, index) => name !== currentNames[index])
        );
    }, [initialDomains, currentDomains]);

    const areCompetitorsChanged = useMemo<boolean>(() => {
        const filteredCurrent = currentCompetitors.filter(({ domain, brands }) => domain && brands?.length);

        if (initialCompetitors.length !== filteredCurrent.length) {
            return true;
        }

        return initialCompetitors.some((initial) => {
            const match = filteredCurrent.find((current) => current.domain?.name === initial.domain?.name);

            if (!match || match.brands?.length !== initial.brands?.length) {
                return true;
            }

            return initial.brands?.some((brand) => !match.brands?.includes(brand)) ?? false;
        });
    }, [initialCompetitors, currentCompetitors]);

    const userId: number = swSettings?.user?.id as number;
    const isOwnerChanged = ownerId !== userId;
    const areModelsChanged = useMemo<boolean>(() => {
        const sortedInitial = initialModels.slice().sort();
        const sortedCurrent = currentModels.slice().sort();

        return (
            sortedInitial.length !== sortedCurrent.length ||
            sortedInitial.some((model, index) => model !== sortedCurrent[index])
        );
    }, [initialModels, currentModels]);

    const isSubscriptionChanged = isWeeklyReportSubscribed !== initialWeeklyReportSubscribed;
    const areSubdomainsChanged = includeSubdomains !== initialIncludeSubdomains;
    const hasFieldChanges =
        isNameChanged ||
        areBrandsChanged ||
        areDomainsChanged ||
        areSubdomainsChanged ||
        areCompetitorsChanged ||
        areModelsChanged ||
        isOwnerChanged;

    const hasChanges = hasFieldChanges || isSubscriptionChanged;
    const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign();
    const { mutate: updateSubscription } = useWeeklyReportSubscription();
    const swRedux = SwRedux.getInstance();

    const resetState = (): void => {
        campaignToEditRef.current = undefined;
        dispatch({ type: "RESET" });
    };

    const handleSubscriptionUpdate = (campaignId: string): void => {
        if (!isSubscriptionChanged) {
            return;
        }

        allTrackers.dispatchTrackEvent(
            "Edit Campaign",
            "click",
            `save/weekly report ${isWeeklyReportSubscribed ? "subscribed" : "unsubscribed"}`,
        );

        updateSubscription(
            { campaignId, subscribe: isWeeklyReportSubscribed },
            {
                onSuccess: () => {
                    swRedux.dispatch(showSuccessToast(translate("llmTracker.campaignQuery.edit.emailUpdates.success")));
                },
                onError: () => {
                    swRedux.dispatch(showErrorToast(translate("llmTracker.campaignQuery.edit.emailUpdates.error")));
                },
            },
        );
    };

    const handleEdit = (): void => {
        const campaignToEdit = campaignToEditRef.current;
        if (
            !campaignToEdit?.id ||
            !campaignToEdit?.name ||
            !campaignName.trim() ||
            !selectedBrands?.length ||
            !currentModels?.length ||
            ownerId === undefined ||
            !hasChanges
        ) {
            return;
        }

        if (isSubscriptionChanged) {
            handleSubscriptionUpdate(campaignToEdit.id);
        }

        if (hasFieldChanges) {
            //track save events
            const saveEventsByField: Record<string, ISaveTrackEvent> = {
                name: { isChanged: isNameChanged },
                brands: { isChanged: areBrandsChanged },
                "owned sites": {
                    isChanged: areDomainsChanged,
                    parameters: { number_of_owned_sites: currentDomains.length },
                },
                models: { isChanged: areModelsChanged },
                owner: { isChanged: isOwnerChanged },
            };

            Object.entries(saveEventsByField).forEach(([key, { isChanged, parameters }]) => {
                if (isChanged) {
                    allTrackers.dispatchTrackEvent("Edit Campaign", "click", `save/${key} changed`, parameters);
                }
            });

            updateCampaign(
                {
                    campaignId: campaignToEdit.id,
                    lastModifiedDate: campaignToEdit.lastModifiedDate,
                    ownerId,
                    name: campaignName.trim(),
                    brands: selectedBrands,
                    domains: getUniqueDomainUrls(currentDomains),
                    chatbots: currentModels,
                    includeSubdomains,
                    competitorBrands: currentCompetitors
                        .filter(({ domain, brands }) => domain && brands?.length)
                        .map(
                            (competitor) =>
                                ({
                                    ...competitor,
                                    domain: competitor.domain?.name ?? "",
                                }) as IUpdateCampaignCompetitor,
                        ),
                },
                {
                    onSuccess: () => {
                        swRedux.dispatch(
                            showSuccessToast(
                                translate("llmTracker.campaignQuery.editCampaign.success", {
                                    campaignName,
                                }),
                            ),
                        );

                        resetState();
                        void proNavigator.updateParams({ settings: undefined }, { reload: false });
                    },
                    onError: (error) => {
                        allTrackers.dispatchTrackEvent(
                            "Edit Campaign",
                            "error",
                            `Edit AI Tracker/${campaignToEdit?.name || "Unknown"}`,
                            {
                                campaignId: campaignToEdit?.id,
                                campaignName: campaignToEdit?.name,
                            },
                        );

                        swRedux.dispatch(
                            showErrorToast(
                                translate("llmTracker.campaignQuery.editCampaign.error", {
                                    campaignName: campaignToEdit?.name || "Unknown",
                                }),
                            ),
                        );

                        Sentry.errorTracker({
                            e: error,
                            message: "Error saving campaign settings",
                            level: "error",
                            params: {
                                extra: {
                                    moduleName: "llm-tracker",
                                    campaignId: campaignToEdit?.id,
                                    errorMessage: error,
                                },
                            },
                        });
                    },
                },
            );
        } else {
            resetState();
            void proNavigator.updateParams({ settings: undefined }, { reload: false });
        }
    };

    const openModal = (campaign: ICampaignModalData | undefined, readOnlyMode: boolean): void => {
        if (!campaign?.id) {
            return;
        }

        const mapCampaignDomainsToWebsiteItems = (domains: ICampaignDomain[] = []): IWebsiteItem[] =>
            domains
                .filter(({ url }) => !!url)
                .map(({ url, favicon }, index) => ({
                    id: `${url}-${index}`,
                    name: url,
                    url,
                    image: favicon || "",
                    displayText: url,
                    searchType: WebsitePageSearchTypeNames.MainDomain,
                }));

        const rawCompetitors =
            campaign.competitors?.map((competitor) => {
                const { url, favicon } = competitor.domain;

                return {
                    ...competitor,
                    domain: {
                        name: url,
                        image: favicon || "",
                        searchType: WebsitePageSearchTypeNames.MainDomain,
                        displayText: url,
                        url,
                    },
                };
            }) ?? [];

        const isWeeklySubscribed = !!campaign?.subscriptions?.includes(AI_TRACKER_WEEKLY_REPORT_SUBSCRIPTION);
        const areSubdomainsIncluded = !!campaign?.includeSubdomains;
        const rawDomains = mapCampaignDomainsToWebsiteItems(campaign.domains);
        const rawModels = Object.keys(campaign?.configs ?? {}).filter(
            (model) => (model as LLModel) !== LLModel.All,
        ) as ExplicitLLModel[];

        campaignToEditRef.current = campaign;
        dispatch({
            type: "OPEN",
            payload: {
                isReadOnly: readOnlyMode,
                campaignName: campaign?.name,
                selectedBrands: campaign?.brands ?? [],
                initialDomains: rawDomains,
                currentDomains: rawDomains,
                initialCompetitors: rawCompetitors,
                currentCompetitors: rawCompetitors.length ? [...rawCompetitors] : [EMPTY_COMPETITOR],
                initialModels: rawModels,
                currentModels: rawModels,
                ownerId: userId,
                initialWeeklyReportSubscribed: isWeeklySubscribed,
                isWeeklyReportSubscribed: isWeeklySubscribed,
                initialIncludeSubdomains: areSubdomainsIncluded,
                includeSubdomains: areSubdomainsIncluded,
            },
        });

        if (!isOpen) {
            void proNavigator.updateParams({ settings: "true" }, { reload: false });
        }
    };

    const closeModal = (): void => {
        void proNavigator.updateParams({ settings: undefined }, { reload: false });
        resetState();
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "edit campaign/cancel");
    };

    const campaignData = campaignToEditRef.current;

    return {
        openModal,
        modal:
            campaignData && isOpen ? (
                <CampaignEditContext.Provider
                    value={{
                        state,
                        dispatch,
                        campaignData,
                        hasChanges,
                        isLoading: isUpdating,
                        onApprove: handleEdit,
                        onClose: closeModal,
                    }}
                >
                    <CampaignSettingsModal />
                </CampaignEditContext.Provider>
            ) : null,
    };
};
